import { Head } from "vite-react-ssg";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import Breadcrumbs from "@/components/Breadcrumbs";
import RelatedLinks from "@/components/RelatedLinks";
import LastUpdated from "@/components/LastUpdated";
import { DEEPSEEK_PRICING } from "@/lib/deepseek-pricing";
import {
  COMPETITOR_PRICING,
  CLAUDE_CONSUMER_PLANS,
  multipleVs,
  percentCheaperThan,
  usd,
} from "@/lib/competitor-pricing";

const TITLE = "DeepSeek vs Claude 2026: Verified Prices, Models & Context";
const DESCRIPTION =
  "DeepSeek V4 vs Claude compared on July 2026 rates — V4-Flash at $0.14/M against Claude Sonnet 5 at $2/M. Both now at 1M context. Every figure sourced.";
const URL = "https://deepseek.ai/deepseek-vs-claude";

const DeepseekVsClaude = () => {
  const flash = DEEPSEEK_PRICING.models["v4-flash"];
  const pro = DEEPSEEK_PRICING.models["v4-pro"];
  const m = COMPETITOR_PRICING.models;
  const sonnet = m["claude-sonnet-5"];
  const opus = m["claude-opus-5"];
  const fable = m["claude-fable-5"];
  const haiku = m["claude-haiku-4.5"];

  const apiRows = [
    { label: "DeepSeek V4-Flash", input: flash.inputCacheMiss, cached: flash.inputCacheHit, output: flash.output, highlight: true },
    { label: "DeepSeek V4-Pro", input: pro.inputCacheMiss, cached: pro.inputCacheHit, output: pro.output, highlight: true },
    { label: haiku.label, input: haiku.input, cached: haiku.cachedInput, output: haiku.output, highlight: false },
    { label: sonnet.label, input: sonnet.input, cached: sonnet.cachedInput, output: sonnet.output, highlight: false },
    { label: opus.label, input: opus.input, cached: opus.cachedInput, output: opus.output, highlight: false },
    { label: fable.label, input: fable.input, cached: fable.cachedInput, output: fable.output, highlight: false },
  ];

  const multiples = [
    {
      comparison: "Sonnet 5 vs V4-Flash",
      input: multipleVs(sonnet.input, flash.inputCacheMiss),
      output: multipleVs(sonnet.output, flash.output),
    },
    {
      comparison: "Opus 5 vs V4-Pro",
      input: multipleVs(opus.input, pro.inputCacheMiss),
      output: multipleVs(opus.output, pro.output),
    },
    {
      comparison: "Fable 5 vs V4-Pro",
      input: multipleVs(fable.input, pro.inputCacheMiss),
      output: multipleVs(fable.output, pro.output),
    },
  ];

  const cheaperInput = percentCheaperThan(sonnet.input, flash.inputCacheMiss);
  const cheaperOutput = percentCheaperThan(sonnet.output, flash.output);
  const change = sonnet.priceChange;

  const faqItems = [
    {
      question: "Which has the bigger context window?",
      answer:
        "Neither — both are at 1M tokens. DeepSeek allows 384K max output against Claude's 128K, which matters for long generation. Claude Haiku 4.5 is the exception at 200K context.",
    },
    {
      question: "Is DeepSeek 80–90% cheaper than Claude?",
      answer: `It is more than that. Against Claude Sonnet 5 at current rates, V4-Flash is about ${cheaperInput} cheaper on input and ${cheaperOutput} cheaper on output. Note that Sonnet 5's introductory pricing ends 31 August 2026, after which the gap widens further.`,
    },
    {
      question: "Can DeepSeek handle images?",
      answer:
        "No. Both V4 models are text-only. Every current Claude model accepts image input.",
    },
    {
      question: "Does DeepSeek have a free API tier?",
      answer:
        "Not a documented one. DeepSeek's API documentation describes concurrency limits — 2,500 for V4-Flash and 500 for V4-Pro — but no free credits or free tier. The consumer chat at chat.deepseek.com is free with an account.",
    },
    {
      question: "Is R1 still DeepSeek's reasoning model?",
      answer:
        "No. R1 is legacy. The current models are V4-Pro and V4-Flash, each with a thinking mode. The deepseek-reasoner alias was retired on 24 July 2026.",
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: TITLE,
    description: DESCRIPTION,
    url: URL,
    inLanguage: "en",
    dateModified: COMPETITOR_PRICING.lastVerified,
    isPartOf: {
      "@type": "WebSite",
      name: "The Independent DeepSeek Guide",
      url: "https://deepseek.ai",
    },
    about: [
      { "@type": "Thing", name: "DeepSeek V4" },
      { "@type": "Thing", name: "Claude" },
    ],
  };

  return (
    <>
      <Head>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={URL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={URL} />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Head>

      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
          <Breadcrumbs currentPage="DeepSeek vs Claude" />

          <main>
            <article className="mt-6">
              <header className="mb-10">
                <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4 leading-tight">
                  DeepSeek vs Claude: The 2026 Price and Capability Gap
                </h1>
                <LastUpdated date={COMPETITOR_PRICING.lastVerified} />
              </header>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-foreground mb-4">The short answer</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    DeepSeek V4-Flash costs {usd(flash.inputCacheMiss)} per million input tokens.
                    Claude Sonnet 5 costs {usd(sonnet.input)}. On output the gap widens to{" "}
                    {usd(flash.output)} against {usd(sonnet.output)} — roughly {cheaperOutput}{" "}
                    cheaper.
                  </p>
                  <p>
                    But the context-window argument that every older comparison makes is dead. Both
                    are at 1M tokens now. If you read somewhere that DeepSeek is limited to 128K and
                    Claude to 200K, that page is describing 2025.
                  </p>
                  <p className="text-sm border-l-2 border-primary/40 pl-4">
                    Last verified 25 July 2026 against DeepSeek's API documentation and Anthropic's
                    model and pricing documentation. We re-check this page weekly.
                  </p>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-foreground mb-4">Current models</h2>

                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">DeepSeek</h3>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Model</TableHead>
                        <TableHead>Total params</TableHead>
                        <TableHead>Active</TableHead>
                        <TableHead>Context</TableHead>
                        <TableHead>Max output</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">deepseek-v4-flash</TableCell>
                        <TableCell>284B</TableCell>
                        <TableCell>~13B</TableCell>
                        <TableCell>1M</TableCell>
                        <TableCell>384K</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">deepseek-v4-pro</TableCell>
                        <TableCell>1.6T</TableCell>
                        <TableCell>~49B</TableCell>
                        <TableCell>1M</TableCell>
                        <TableCell>384K</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                <p className="text-muted-foreground mt-4">
                  Text-only, MoE, MIT licence, open weights. Both offer thinking and non-thinking
                  modes. The legacy deepseek-chat and deepseek-reasoner aliases were retired on 24
                  July 2026 —{" "}
                  <Link
                    to="/blog/deepseek-chat-reasoner-retired-billing-impact"
                    className="text-primary hover:underline font-medium"
                  >
                    see the migration guide
                  </Link>
                  . More on the architecture in{" "}
                  <Link to="/deepseek-v4" className="text-primary hover:underline font-medium">
                    DeepSeek V4 explained
                  </Link>
                  .
                </p>


                <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">Anthropic</h3>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Model</TableHead>
                        <TableHead>Context</TableHead>
                        <TableHead>Max output</TableHead>
                        <TableHead>Thinking</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">{fable.label}</TableCell>
                        <TableCell>1M</TableCell>
                        <TableCell>128K</TableCell>
                        <TableCell>Adaptive, always on</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">{opus.label}</TableCell>
                        <TableCell>1M</TableCell>
                        <TableCell>128K</TableCell>
                        <TableCell>Adaptive</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">{sonnet.label}</TableCell>
                        <TableCell>1M</TableCell>
                        <TableCell>128K</TableCell>
                        <TableCell>Adaptive</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">{haiku.label}</TableCell>
                        <TableCell>200K</TableCell>
                        <TableCell>64K</TableCell>
                        <TableCell>Extended</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                <p className="text-muted-foreground mt-4">
                  All current Claude models take text and image input and produce text output. That
                  is the first real capability difference: Claude sees images, DeepSeek does not.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-foreground mb-4">API pricing</h2>
                <p className="text-muted-foreground mb-4">Per 1M tokens:</p>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Model</TableHead>
                        <TableHead>Input</TableHead>
                        <TableHead>Cache read</TableHead>
                        <TableHead>Output</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {apiRows.map((r) => (
                        <TableRow key={r.label} className={r.highlight ? "bg-primary/5" : undefined}>
                          <TableCell className="font-medium">{r.label}</TableCell>
                          <TableCell>{usd(r.input)}</TableCell>
                          <TableCell>{r.cached === null ? "—" : usd(r.cached)}</TableCell>
                          <TableCell>{usd(r.output)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-muted-foreground mt-6">
                  One thing to diarise: Claude Sonnet 5's price rises on 1 September 2026. The{" "}
                  {usd(sonnet.input)}/{usd(sonnet.output)} rate is introductory pricing running
                  through 31 August; from 1 September the standard rate of {usd(change.input)} input
                  / {usd(change.output)} output applies. Anthropic states this on its own pricing
                  page. If you are modelling costs for Q4, use the September numbers.
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">The multiples</h3>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Comparison</TableHead>
                        <TableHead>Input</TableHead>
                        <TableHead>Output</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {multiples.map((r) => (
                        <TableRow key={r.comparison}>
                          <TableCell className="font-medium">{r.comparison}</TableCell>
                          <TableCell>{r.input} more</TableCell>
                          <TableCell>{r.output} more</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-muted-foreground mt-4">
                  Put as a percentage, since that is how most comparisons phrase it: against Sonnet
                  5, DeepSeek V4-Flash is about {cheaperInput} cheaper on input and {cheaperOutput}{" "}
                  cheaper on output. Comparisons claiming "80–90% cheaper" are understating it. Our{" "}
                  <Link to="/pricing" className="text-primary hover:underline font-medium">
                    DeepSeek pricing page
                  </Link>{" "}
                  has the full rate card and a cost calculator.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-foreground mb-4">Consumer plans</h2>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Claude</TableHead>
                        <TableHead>Price</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {CLAUDE_CONSUMER_PLANS.plans.map((p) => (
                        <TableRow key={p.name}>
                          <TableCell className="font-medium">{p.name}</TableCell>
                          <TableCell className="text-muted-foreground">{p.price}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-muted-foreground mt-4">
                  Pro and above include Claude Code and Claude Cowork. DeepSeek's consumer chat is
                  free with an account and has no paid tier.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-foreground mb-4">Where each one wins</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    <strong className="text-foreground">DeepSeek</strong> when cost per token
                    dominates, when your workload is text at volume, when you want open weights you
                    can run yourself, or when you need 1M context at a flat rate.
                  </p>
                  <p>
                    <strong className="text-foreground">Claude</strong> when you need image input,
                    when you want adaptive thinking that decides its own reasoning depth, when you
                    are doing agentic or coding work with Claude Code, or when your organisation
                    needs the enterprise controls Anthropic ships.
                  </p>
                  <p>
                    The models are closer than the price gap suggests on plain text generation, and
                    further apart than it suggests on anything agentic or visual.
                  </p>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-foreground mb-4">What we do not claim</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    We do not publish a star rating, a benchmark table, or a language count for
                    either model.
                  </p>
                  <p>
                    <strong className="text-foreground">On benchmarks:</strong> the scores circulating
                    for these models mostly cannot be traced to the vendor or to a named leaderboard.
                    Artificial Analysis publishes dated, documented comparisons — use those.
                    Anthropic publishes evaluations in its own release posts.
                  </p>
                  <p>
                    <strong className="text-foreground">On languages:</strong> you will see "Claude
                    supports 70+ languages" and "DeepSeek supports 50+" on comparison pages across
                    the web. Neither company publishes a total. Anthropic's multilingual
                    documentation benchmarks 15 languages and says Claude is capable in many more;
                    DeepSeek publishes no count at all. Both numbers are invented.
                  </p>
                  <p>
                    <strong className="text-foreground">On speed:</strong> we have not published
                    response-time figures because we have no methodology we would defend.
                  </p>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-foreground mb-4">FAQ</h2>
                <Accordion type="single" collapsible className="w-full">
                  {faqItems.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`}>
                      <AccordionTrigger className="text-left font-medium">
                        {f.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed">
                        {f.answer}
                        {f.question.startsWith("Is R1") && (
                          <>
                            {" "}
                            <Link
                              to="/blog/deepseek-chat-reasoner-retired-billing-impact"
                              className="text-primary hover:underline font-medium"
                            >
                              Full migration guide →
                            </Link>
                          </>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>

              <RelatedLinks />
            </article>
          </main>
        </div>
      </div>
    </>
  );
};

export default DeepseekVsClaude;
